import {
  PaymentAmount,
  PaymentData,
  PaymentResponseData,
  CheckoutAdvancedFlowResponse
} from "@adyen/adyen-web"
import { AdditionalData3DSecure } from "@adyen/api-library/lib/src/typings/payment/additionalData3DSecure"
import { Cart } from "@commercetools/platform-sdk"
import { AdyenSession } from "../checkout/payment/PaymentButtons"
import {
  AdyenPaymentMethod,
  AdyenPaymentMethodType
} from "../checkout/payment/validation/AdyenPaymentValidation"
import { noop } from "../utils/FunctionUtilis"

const paymentApiAdyenUri: (path: string) => string = path =>
  `/api/v1/payment/adyen${path}`

export class AdyenBrowserClient {
  private readonly tokenProvider: () => string
  constructor(tokenProvider: () => string) {
    this.tokenProvider = tokenProvider
  }

  private post = <T>(
    uri: string,
    body: Record<string, unknown>,
    defaultResponseBody: T
  ): Promise<T> =>
    fetch(uri, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        authorization: `Bearer ${this.tokenProvider()}`
      },
      body: JSON.stringify(body)
    }).then(it => {
      if (it.headers.get("Content-Length") === "0") return defaultResponseBody
      return it.json() || defaultResponseBody
    })

  async initialise(cart: Cart): Promise<{
    paymentId?: string
    paymentMethods: AdyenPaymentMethod[]
    session?: AdyenSession
    amount: PaymentAmount
  }> {
    const result = await this.post<{
      paymentId?: string
      paymentMethods: AdyenPaymentMethod[]
      session?: AdyenSession
      amount: PaymentAmount
    }>(
      paymentApiAdyenUri(`/session`),
      {
        cartId: cart?.id
      },
      {
        paymentId: undefined,
        paymentMethods: [],
        session: undefined,
        amount: {
          value: 0,
          currency: ""
        }
      }
    )
    return {
      ...result
    }
  }

  pay(
    paymentId: string,
    paymentRequest: PaymentData & { returnUrl?: string }
  ): Promise<PaymentResponseData> {
    return this.post(
      paymentApiAdyenUri(`/pay`),
      {
        paymentId,
        paymentRequest
      },
      {
        resultCode: "Received",
        action: undefined,
        status: undefined,
        sessionData: paymentRequest.sessionData || ""
      }
    )
  }

  async paymentMethod(
    paymentId: string,
    paymentMethod: AdyenPaymentMethodType
  ): Promise<void> {
    return await this.post(
      paymentApiAdyenUri("/method"),
      {
        paymentId,
        method: paymentMethod
      },
      {}
    ).then(noop)
  }

  details(
    details: AdditionalData3DSecure,
    paymentData?: string
  ): Promise<CheckoutAdvancedFlowResponse> {
    return this.post(
      paymentApiAdyenUri("/details"),
      {
        details,
        paymentData
      },
      {
        sessionData: "",
        sessionResult: "",
        resultCode: "Received",
        status: undefined,
        action: undefined
      }
    )
  }
}
